import { useEffect, useMemo, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAssociateAttributionEventWithUser, useCreateAttributionEvent } from '~/api/attribution_events_queries'
import { AttributionEvent } from '../types/attribution_event'
import { User } from '../types/user'
import { Empty } from '../types/utility'

const ATTRIBUTION_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_content',
  'utm_campaign',
  'utm_term',
  'gclid',
  'gbraid',
  'wbraid',
  'actor_id',
  'actor_type',
] as const

type AttributionKey = typeof ATTRIBUTION_KEYS[number]
type AttributionParams = Partial<Record<AttributionKey, string>>

// don't remove too quickly, as GA4 may not have fully captured the params yet.
// This is especially important for users with slower connections
const GA4_SAFE_URL_CLEANUP_DELAY_MS = 2500

const getAttributionParamsFromSearch = (search: string): AttributionParams | null => {
  const params = new URLSearchParams(search)
  const attributionParams: AttributionParams = {}

  ATTRIBUTION_KEYS.forEach((key) => {
    const value = params.get(key)
    if (value) attributionParams[key] = value
  })

  if (Object.keys(attributionParams).length === 0) {
    return null
  }

  return attributionParams
}

// looks for attribution params in the url, and if they exist,
// creates an AttributionEvent, then removes the params from the url
// tracking utm and other attribution params for users allows us to segment them,
// understand where they are coming from etc informing marketing and product decisions
export default function useUrlAttributionTracking({
  sessionUser,
}: {
  sessionUser: User | Empty
}) {
  const location = useLocation()
  const navigate = useNavigate()
  const createAttributionEventQuery = useCreateAttributionEvent()
  const associateAttributionEventQuery = useAssociateAttributionEventWithUser()
  const handledParamsRef = useRef<string[]>([])
  const attributionEvent = useRef<AttributionEvent | null>(null)

  const urlAttributionParams = useMemo(
    () => getAttributionParamsFromSearch(location.search),
    [location.search]
  )

  const clearUtmParamsFromUrl = () => {
    navigate(
      {
        pathname: location.pathname,
        search: '',
        hash: location.hash,
      },
      { replace: true }
    )
  }

  // TODO: Some situations (business cards, cold emails etc) won't have a user yet, 
  //       Currently, if that user signs in or registers after arriving, we don't yet try to 
  //       associate the attribution_event with the user. We should do this eventually,
  //       and that would entail storing the attribution event after create in state
  //       and observing the sessionUser, and if it changes from null to a user, 
  //       we should then associate the attribution_event with that user.
  useEffect(() => {

    if (urlAttributionParams === null) return
    if (handledParamsRef.current.includes(location.search)) return

    handledParamsRef.current.push(location.search)

    window.setTimeout(() => clearUtmParamsFromUrl(), GA4_SAFE_URL_CLEANUP_DELAY_MS)

    const attributionEventPromise = createAttributionEventQuery.mutateAsync({
      ...urlAttributionParams,
      landing_url: window.location.href
    })

    attributionEventPromise.then((event) => {
      attributionEvent.current = event
    })

  }, [urlAttributionParams])

  // associate the attribution event with the user, if it becomes available after the event is created
  useEffect(() => {
    if (
      sessionUser === null ||
      attributionEvent.current === null ||
      attributionEvent.current.actor_id
    ) return

    const attributionEventPromise = associateAttributionEventQuery.mutateAsync({
      slug: attributionEvent.current.slug
    })

    attributionEventPromise.then((event) => {
      attributionEvent.current = event
    })
  }, [sessionUser])
}
